import styled, { css } from 'styled-components';
import { size } from '../../layout/helpers';
import { RouterLink } from '../Link';

export const CardBody = styled.div.withConfig({ componentId: 'sc-124l8s8-0' })`
    display: flex;
    flex-direction: column;
    gap: ${size(1)};
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
`;

export const ImageContainer = styled.div.withConfig({ componentId: 'sc-124l8s8-1' })`
    position: relative;
    flex: 0 0 auto;
    aspect-ratio: 343 / 199;
    width: 100%;
    background: var(--c-bg-alt);

    ${({ theme }) => theme.breakpoint.Md} {
        aspect-ratio: 417 / 234;
    }
`;

export const VideoIcon = styled.div.withConfig({ componentId: 'sc-124l8s8-2' })`
    --_size: ${size(7)};
    --_icon-size: ${size(3)};

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--_size);
    height: var(--_size);
    border-radius: 50%;
    background-color: var(--c-primary-bg);
    color: var(--c-primary-fg);
    font-size: var(--_icon-size);
`;

export const Image = styled.img.withConfig({ componentId: 'sc-124l8s8-3' })`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

export const Eyebrow = styled.div.withConfig({ componentId: 'sc-124l8s8-4' })`
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-xsmall);
    letter-spacing: 0.15em;
    text-transform: uppercase;
    opacity: 0.8;
`;

export const Excerpt = styled.p.withConfig({ componentId: 'sc-124l8s8-5' })`
    margin: 0;
`;

export const Category = styled.div.withConfig({ componentId: 'sc-124l8s8-6' })`
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-bold);
    color: var(--c-secondary-fg);
`;

export const Title = styled(RouterLink).withConfig({ componentId: 'sc-124l8s8-7' })`
    margin: 0;
    color: var(--c-text-dark);
    font-weight: var(--font-weight-bold);

    &::before {
        content: "";
        position: absolute;
        inset: 0;
    }
`;

export const TimeRow = styled.div.withConfig({ componentId: 'sc-124l8s8-8' })`
    display: flex;
    gap: ${size(3)};
    font-size: var(--font-size-small);
    margin-top: ${size(1)};
    color: var(--c-text-muted);

    > * + * {
        position: relative;

        &:before {
            content: "";
            position: absolute;
            top: calc(50% - ${size(0.25)});
            left: ${size(-1.75)};
            height: ${size(0.5)};
            width: ${size(0.5)};
            border-radius: ${size(0.5)};
            background: var(--c-text-muted);
        }
    }
`;

export const AuthorRow = styled.div.withConfig({ componentId: 'sc-124l8s8-9' })`
    display: flex;
    gap: ${size(1)};
    align-items: center;
    margin-top: ${size(1)};
`;

export const AuthorImage = styled.img.withConfig({ componentId: 'sc-124l8s8-10' })`
    border-radius: 50%;
    width: ${size(4)};
    height: ${size(4)};
`;

export const AuthorName = styled.div.withConfig({ componentId: 'sc-124l8s8-11' })`
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    color: var(--c-text-dark);
`;

interface IArticleCardWrapper {
  $layout: 'large' | 'medium' | 'small' | 'stacked';
  $mirrored?: boolean;
  $backplate?: boolean;
}

export const Wrapper = styled.div.withConfig({ componentId: 'sc-124l8s8-12' })<IArticleCardWrapper>`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${size(2)};
    align-items: center;

    ${({ $layout }: IArticleCardWrapper) =>
      $layout === 'large' &&
      css`
            ${Title} {
                font-size: var(--font-size-h2);
                line-height: var(--line-height-h2);
            }
        `}

    ${({ $layout }: IArticleCardWrapper) =>
      $layout === 'small' &&
      css`
            flex-direction: row;

            ${ImageContainer} {
                width: ${size(11)};
                aspect-ratio: 1 / 1;
            }

            ${TimeRow} {
                margin-top: 0;
            }
        `}

    ${({ $layout }: IArticleCardWrapper) =>
      $layout === 'stacked' &&
      css`
            align-items: flex-start;

            ${ImageContainer} {
                width: 100%;
            }
        `}

    ${({ theme }) => theme.breakpoint.LtMd} {
        ${({ $layout }: IArticleCardWrapper) =>
          $layout === 'small' &&
          css`
                ${Excerpt} {
                    display: none;
                }
            `}
        ${({ $layout }: IArticleCardWrapper) =>
          ($layout === 'large' || $layout === 'medium') &&
          css`
                ${ImageContainer} {
                    width: 100%;
                }
            `}
    }

    ${({ theme }) => theme.breakpoint.Md} {
        ${({ $layout }: IArticleCardWrapper) =>
          $layout === 'large' &&
          css`
                gap: ${size(5)};
                flex-direction: row;

                ${ImageContainer} {
                    aspect-ratio: 632 / 460;
                    width: calc(50% - ${size(2.5)});
                }

                ${CardBody} {
                    padding: ${size(1)} ${size(5)} ${size(1)} 0;
                }
            `}

        ${({ $layout }: IArticleCardWrapper) =>
          $layout === 'medium' &&
          css`
                flex-direction: row;

                ${ImageContainer} {
                    max-width: 416px;
                }
            `}

        ${({ $layout }: IArticleCardWrapper) =>
          $layout === 'small' &&
          css`
                ${ImageContainer} {
                    width: ${size(18)};
                }

                ${Excerpt} {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            `}
    }

    ${({ $mirrored, $layout }: IArticleCardWrapper) =>
      $mirrored && $layout !== 'medium'
        ? css`
                  ${ImageContainer} {
                      order: 2;
                  }
              `
        : $mirrored
          ? css`
                  ${({ theme }) => theme.breakpoint.Md} {
                      ${ImageContainer} {
                          order: 2;
                      }
                  }
              `
          : undefined}

    ${({ $backplate }: IArticleCardWrapper) =>
      $backplate &&
      css`
            background: var(--c-secondary-bg);

            ${({ theme }) => theme.breakpoint.LtMd} {
                ${CardBody} {
                    padding: ${size(2)};
                }
            }
        `}
`;
